import { UIElement } from '../ui-element';
import { createElement } from '../../global/render-api';
import { Digest } from '../../global/digest';
import { rebuild } from '../../global/mutations/index';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { CmsAttribute } from '../../decorators/cms-attribute.decorator';
import { CMS_VALUE_TYPES, CMS_CONTENT_MODES } from '../../cms';
import type { TranslationLabels } from '../../types/labels.type';
import { Child } from '../../decorators/child.decorator';
import { ElementConfig } from '../ui-element.type';
import type { UISurfaceColor } from '../../types/color.type';
import styles from './ui-product.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIProduct
 * @element ui-product
 * @classdesc Represents a class for <code>ui-product</code> element.
 * Should be child of ui-products container.
 * @fires event:product-open
 * @property {string} [pageId] {@attr page-id} Page ID where it will be redirected after
 * click on product. Should be in iBank's format eg. 'private.home.more'.
 * @property {string} [externalUrl] {@attr external-url} URL where you will be redirected
 * after click.
 * @property {UISurfaceColor} [background] {@attr background} Sets background surface color.
 *  {@desc neutral: Neutral color}
 *  {@desc primary: Primary color}
 *  {@desc secondary: Secondary color - Default}
 *  {@desc tertiary: Tertiary color}
 *  {@desc outlined: Outlined neutral color}
 * @property {string} [labelLink] {@attr label-link} Label for link.
 * @property {string} [label] {@attr label} Title of product.
 * @property {string} [tagline] {@attr tagline} Tagline of product.
 * @property {('contain')} [image] {@attr image} Image container resize attribute.
 * @property {HTMLAnchorElement} [link] Read-only. Shortcut to first link in product.
 * @property {HTMLElement} header Read-only. Shortcut to header.
 * @property {HTMLElement} figure Read-only. Shortcut to figure.
 * @property {HTMLElement} article Read-only. Shortcut to article.
 * @slot
 * @example
 * <ui-product pageId="private.home.more" title="Hi" icon="transport">
 *   <p>This is a cool product</p>
 * </ui-product>
 */
@Component({
    tag: 'ui-product',
    styles,
    cms: {
        mode: CMS_CONTENT_MODES.Children,
    },
})
export class UIProduct extends UIElement {
    @Attribute(String) pageId?: string;
    @CmsAttribute({ valueType: CMS_VALUE_TYPES.Url })
    @Attribute(String)
    externalUrl?: string;
    @Attribute(String) background?: UISurfaceColor;
    @Attribute(String) labelLink?: string;
    @Attribute(String) label?: TranslationLabels.Label;
    @Attribute(String) tagline?: TranslationLabels.Label;
    @CmsAttribute({ valueType: CMS_VALUE_TYPES.Url })
    @Attribute(String)
    image?: TranslationLabels.Label;
    @Child('a') link!: HTMLAnchorElement;
    @Child('h3') header!: HTMLHeadingElement;
    @Child('figure') figure!: HTMLElement;
    @Child('article') article!: HTMLElement;

    private productSelectionHandler!: EventListener;

    /**
     * @private
     * @returns {Record<string, string | null>}
     */
    private getProductOpenParams(): Record<string, string | null> {
        const params: Record<string, string | null> = {};
        if (
            this.link?.getAttribute('href') &&
            !this.isGeneratedLink(this.link)
        ) {
            params['external-url'] = this.link.getAttribute('href');
        }
        if (this.pageId) {
            params['page-id'] = this.pageId;
        }
        if (this.externalUrl) {
            params['external-url'] = this.externalUrl;
        }

        return params;
    }

    /**
     * @private
     * @returns {boolean}
     */
    private hasSlottedLink(): boolean {
        return Boolean(this.link || this.querySelector('ui-link[href]'));
    }

    /**
     * @private
     * @returns {boolean}
     */
    private shouldRenderDefaultLink(): boolean {
        return Boolean(
            this.labelLink &&
            (this.pageId || this.externalUrl || this.hasAttribute('onclick')) &&
            !this.hasSlottedLink()
        );
    }

    /**
     * @private
     * @returns {boolean}
     */
    private shouldRenderInnerButton(): boolean {
        return Boolean(
            (this.pageId || this.externalUrl) &&
            !this.labelLink &&
            !this.hasSlottedLink()
        );
    }

    /**
     * @private
     * @param {HTMLAnchorElement | null} link
     * @returns {boolean}
     */
    private isGeneratedLink(link: HTMLAnchorElement | null): boolean {
        return Boolean(link?.hasAttribute('data-ui-product-link'));
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): Array<string> {
        return ['label'];
    }

    /**
     * Handles product action when either the host is clicked, a generated CTA link, or a slotted link.
     * Slotted links keep their native action and must not dispatch product-open.
     * Product actions are always exposed through a natively focusable control: either an
     * inner wrapper button or a CTA link.
     * @param {Event} event
     * @private
     */
    private handleProductSelection(event: Event): void {
        const clickedLink = (event.target as Element).closest('a');
        if (clickedLink && !this.isGeneratedLink(clickedLink)) {
            return;
        }

        if (clickedLink) {
            event.preventDefault();
        }

        this.dispatchCustomEvent('product-open', this.getProductOpenParams());
    }

    /**
     * @private
     * @returns {ElementConfig}
     */
    private buildHeader(): ElementConfig {
        return {
            tagName: 'h3',
            children: [this.label ?? ''],
        };
    }

    /**
     * @private
     * @returns {ElementConfig}
     */
    private buildTagline(): ElementConfig {
        return {
            tagName: 'p',
            classList: {
                highlight: true,
            },
            children: [this.tagline ?? ''],
        };
    }

    /**
     * @private
     * @returns {ElementConfig}
     */
    private buildInnerWrapper(): ElementConfig {
        const shouldRenderInnerButton = this.shouldRenderInnerButton();

        return {
            tagName: shouldRenderInnerButton ? 'button' : 'div',
            attributes: {
                type: shouldRenderInnerButton ? 'button' : null,
            },
            classList: {
                'ui-product__inner': true,
            },
            children: [...this.childNodes],
        };
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: string | null,
        newValue: string | null
    ): void {
        if (oldValue === newValue) {
            return;
        }

        switch (name) {
            case 'label':
                if (this.header) {
                    rebuild(this.header, this.buildHeader());
                }
                break;
            default:
                break;
        }
    }

    /**
     * @private
     */
    private addEventListeners() {
        this.productSelectionHandler = (event: Event) =>
            this.handleProductSelection(event);
        this.addEventListener('click', this.productSelectionHandler);
    }

    /**
     * @private
     */
    private removeEventListeners() {
        this.removeEventListener('click', this.productSelectionHandler);
    }

    /**
     * @inheritDoc
     */
    render() {
        this.role = 'listitem';
        if (this.label) {
            const headerElement = createElement(this.buildHeader())!;
            this.firstChild
                ? this.insertBefore(headerElement, this.firstChild)
                : this.append(headerElement);
        }
        if (this.tagline) {
            const taglineElement = createElement(this.buildTagline())!;
            this.figure
                ? this.figure.insertAdjacentElement('afterend', taglineElement)
                : this.header
                  ? this.header.insertAdjacentElement(
                        'afterend',
                        taglineElement
                    )
                  : this.append(taglineElement);
        }

        if (this.shouldRenderDefaultLink()) {
            const link: ElementConfig = {
                tagName: 'a',
                attributes: {
                    'href': this.externalUrl ?? '#',
                    'id': Digest.randomId(),
                    'data-ui-product-link': 'true',
                },
                classList: {
                    'default-link': true,
                },
                children: [this.labelLink ?? ''],
            };
            this.insertElements([link]);
        }

        this.insertElements([this.buildInnerWrapper()]);
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.addEventListeners();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.removeEventListeners();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.addEventListeners();
    }
}
